"use strict";
/**
 * Baseball half-inning + offense/defense controls.
 *
 * The scoreboard this file used to drive (score, edit modal, revert) was
 * removed as unused; what remains is the part the ORDER BUTTONS depend on.
 * `offenseTeam` decides which team shows hit buttons and which shows out
 * buttons (`ui/baseball-mode.ts` updateBaseballOffenseDefense), and when it is
 * null the offense picker replaces the buttons entirely — so this is order-path
 * UI, not decoration.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupInningControls = setupInningControls;
exports.setupOffenseControls = setupOffenseControls;
const state_1 = require("../../../state");
const ui_1 = require("../../../ui");
const event_type_service_1 = require("../../../services/event-type-service");
/** Offense alternates every half-inning; null (unpicked) stays unpicked. */
const toggleOffenseTeam = (current) => (current === null ? null : current === "a" ? "b" : "a");
/**
 * Move the clock to `to` and swap offense/defense. Offense is a function of the
 * half-inning, so stepping either way toggles it — that is the whole reason
 * these controls exist (the hit/out button split follows offenseTeam).
 */
function applyHalfInning(eventId, to, offense) {
    const nextOffense = toggleOffenseTeam(offense);
    (0, event_type_service_1.updateBaseballState)(eventId, {
        inning: to.inning,
        halfInning: to.halfInning,
        offenseTeam: nextOffense,
    });
    (0, ui_1.updateInningDisplay)(to.inning, to.halfInning);
    (0, ui_1.updateHalfInningIndicator)(to.halfInning);
    (0, ui_1.updateBaseballOffenseDefense)(nextOffense);
}
/** The baseball entry for the selected event, or null when there isn't one. */
function currentBaseballEvent() {
    const eventId = (0, state_1.getState)().selectedEventTicker;
    if (!eventId)
        return null;
    const entry = (0, event_type_service_1.getEventType)(eventId);
    if (!entry || entry.type !== "baseball")
        return null;
    return { eventId, entry };
}
/**
 * Wire the half-inning advance and its inverse. One tap on ▶½ moves top →
 * bottom → next top AND swaps offense/defense, which is the reason this control
 * exists: without it the operator would have to reset the offense picker and
 * re-pick every half. ◀½ steps back the same way, for a mis-tap on ▶½ while the
 * inning is still going — it is the exact inverse, so it needs no snapshot and
 * works after a refresh or an event switch.
 */
function setupInningControls() {
    const nextHalfInningBtn = document.getElementById("next-half-inning-btn");
    const prevHalfInningBtn = document.getElementById("prev-half-inning-btn");
    const advanceHalfInning = () => {
        const current = currentBaseballEvent();
        if (!current)
            return;
        applyHalfInning(current.eventId, (0, event_type_service_1.nextHalfInning)(current.entry), current.entry.offenseTeam);
    };
    const retreatHalfInning = () => {
        const current = currentBaseballEvent();
        if (!current)
            return;
        // null at the top of the 1st; the button is disabled there, but a stale
        // enabled state must not be able to run the clock past the start.
        const to = (0, event_type_service_1.previousHalfInning)(current.entry);
        if (!to)
            return;
        applyHalfInning(current.eventId, to, current.entry.offenseTeam);
    };
    nextHalfInningBtn?.addEventListener("click", () => advanceHalfInning());
    prevHalfInningBtn?.addEventListener("click", () => retreatHalfInning());
}
// ---------------------------------------------------------------------------
// Offense team selection
// ---------------------------------------------------------------------------
/** Wire the offense-team picker and its reset. */
function setupOffenseControls() {
    const pickOffenseA = document.getElementById("pick-offense-a");
    const pickOffenseB = document.getElementById("pick-offense-b");
    const resetOffenseBtn = document.getElementById("reset-offense-btn");
    const setOffense = (team) => {
        const state = (0, state_1.getState)();
        const eventId = state.selectedEventTicker;
        if (!eventId)
            return;
        (0, event_type_service_1.updateBaseballState)(eventId, { offenseTeam: team });
        (0, ui_1.updateBaseballOffenseDefense)(team);
        (0, ui_1.updateResetOffenseVisibility)(true);
    };
    pickOffenseA?.addEventListener("click", () => setOffense("a"));
    pickOffenseB?.addEventListener("click", () => setOffense("b"));
    // Reset offense selection (revert to picker)
    resetOffenseBtn?.addEventListener("click", () => {
        const state = (0, state_1.getState)();
        const eventId = state.selectedEventTicker;
        if (!eventId)
            return;
        (0, event_type_service_1.updateBaseballState)(eventId, { offenseTeam: null });
        (0, ui_1.updateBaseballOffenseDefense)(null);
        (0, ui_1.updateResetOffenseVisibility)(false);
    });
}
